import { BLOCKS } from '@contentful/rich-text-types'
import IconCheckCircle from '@fortuneo/ui-icons/general/IconCheckCircle'
import IconContentCopy from '@fortuneo/ui-icons/general/IconContentCopy'
import { withDisplayName } from '@hocs/with-display-name'
import LegalMentionsDialog from '@layouts/legal-mentions-dialog'
import { documentPromoHighlightRefresh } from '@render-node/document-promo-highlight-refresh'
import { children } from '@render-node/utils/children'
import RichText from '@resolvers/rich-text'
import { defaultRichTextFieldOptionsDeprecated } from '@resolvers/rich-text/default-rich-text-field-options-deprecated'
import { Tooltip } from '@ui/tooltip'
import { formatDate } from '@utils/date'
import { clsx } from 'clsx/lite'
import { graphql } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import React, { useCallback, useState } from 'react'

const ContentfulOfferRefreshHome: Utils.EntryFC<
  Queries.ContentfulOfferRefreshFragment
> = ({
  content: {
    image,
    promoNumber,
    promoSymbol,
    promoBeginning,
    promoEnd,
    promoCode,
    promoTextV2,
    modalConditions,
    promoHighlightV2,
    baseLine,
  },
  entryInspectorProps,
  fieldInspectorProps,
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [hasCopied, setHasCopied] = useState(false)
  const gatsbyImage = getImage(image)

  const hasImage = Boolean(gatsbyImage)

  const close = () => {
    setIsOpen(false)
  }

  const open = () => {
    setIsOpen(true)
  }

  const copyToClipboard = async (valueToCopy: string) =>
    navigator.clipboard.writeText(valueToCopy)

  const handleCopyClick = useCallback(async () => {
    await copyToClipboard(promoCode)
    setHasCopied(true)
  }, [])

  const Icon = hasCopied ? IconCheckCircle : IconContentCopy

  return (
    <div
      className={clsx(
        'h-fit w-full max-w-[824px] rounded-[12px] p-6 laptop:p-12',
        'group-[.is-refresh-product]/page-image-title:border',
        'group-[.is-refresh-product]/page-image-title:border-solid',
        'group-[.is-refresh-product]/page-image-title:border-theme-default-accent2',
        'group-[.is-refresh-product]/page-image-title:bg-background-default'
      )}
      {...entryInspectorProps()}
    >
      <div>
        {promoEnd && (
          <p className="text-095 py-2 text-inherit">
            {promoBeginning ? `Du ` : "Jusqu'"}
            {promoBeginning && (
              <span className="group-[.is-refresh-product]/page-image-title:!font-medium">
                {formatDate(promoBeginning)}{' '}
              </span>
            )}
            {'au '}
            <span className="group-[.is-refresh-product]/page-image-title:!font-medium">
              {formatDate(promoEnd)}
            </span>
          </p>
        )}
        {(promoNumber || promoSymbol || baseLine) && (
          <p className="py-2 text-260 font-medium text-inherit">
            {promoNumber}
            {promoSymbol}
            {baseLine && (
              <>
                &nbsp;
                <span
                  className="block py-2 text-130 font-medium text-inherit laptop:inline laptop:py-0"
                  {...fieldInspectorProps({
                    fieldId: 'baseline',
                  })}
                >
                  {baseLine}
                </span>
              </>
            )}
          </p>
        )}
        {promoHighlightV2 && (
          <RichText
            className="!text-inherit"
            {...fieldInspectorProps({
              fieldId: 'promoHighlightV2',
            })}
            content={promoHighlightV2}
            options={{
              ...defaultRichTextFieldOptionsDeprecated,
              renderNode: {
                ...defaultRichTextFieldOptionsDeprecated.renderNode,
                [BLOCKS.DOCUMENT]: documentPromoHighlightRefresh,
                [BLOCKS.PARAGRAPH]: children,
              },
            }}
          />
        )}
        <RichText
          className="inline"
          {...fieldInspectorProps({
            fieldId: 'promoTextV2',
          })}
          content={promoTextV2}
          options={defaultRichTextFieldOptionsDeprecated}
        />
        {modalConditions && (
          <div className="inline">
            <button
              className="font-medium underline"
              type="button"
              onClick={open}
            >
              Voir conditions
            </button>
            <LegalMentionsDialog
              legalMentions={modalConditions}
              visible={isOpen}
              onClose={close}
            />
          </div>
        )}
        {promoCode && (
          <div className="flex flex-col items-center pb-3 pt-5 mobileL:flex-row">
            <p className="whitespace-pre py-1 text-085 text-inherit">
              Code opération :
            </p>
            <Tooltip title="Copier le code promo">
              <button className="flex" type="button" onClick={handleCopyClick}>
                <p className="px-2 py-1 text-115 font-medium leading-[1rem] text-inherit">
                  {promoCode}
                </p>
                <Icon
                  className="self-center !fill-inherit"
                  fill="#FFF"
                  size={16}
                />
              </button>
            </Tooltip>
          </div>
        )}
      </div>
      {hasImage && (
        <div
          className="hidden max-w-[326px] justify-self-center"
          {...fieldInspectorProps({
            fieldId: 'image',
          })}
        >
          <GatsbyImage
            alt={image.description}
            image={gatsbyImage}
            loading="eager"
          />
        </div>
      )}
    </div>
  )
}

export const query = graphql`
  fragment ContentfulOfferRefresh on Contentful10SSpX3YFi0CgK40EseM2G {
    ...ContentfulEntry
    modalConditions {
      rich
      references {
        ...ContentfulFootnoteSup
      }
    }
    image {
      description
      gatsbyImageData(formats: [AVIF, WEBP, AUTO])
    }
    promoBeginning
    promoCode
    promoEnd
    promoHighlightV2 {
      rich
      references {
        ...ContentfulFootnoteSup
      }
    }
    promoNumber
    promoSymbol
    promoTextV2 {
      rich
      references {
        ...ContentfulFootnoteSup
      }
    }
    baseLine
  }
`

export default withDisplayName(
  ContentfulOfferRefreshHome,
  'ContentfulOfferRefreshHome'
)
